import React from 'react';
import { Eye, FileText, Link2 } from 'lucide-react';
import { Input, TipTapEditor } from '../ui';

interface Props {
  noteHtml: string;
  onNoteChange: (value: string) => void;
  evidenceUrl?: string;
  onEvidenceUrlChange?: (value: string) => void;
  noteError?: string;
  evidenceError?: string;
  visibilityHint: string;
  disabled?: boolean;
  noteLabel?: string;
  noteRequired?: boolean;
  notePlaceholder?: string;
  noteHelperText?: string;
  guidanceTitle?: string;
  guidanceItems?: string[];
  accessTitle?: string;
  accessHint?: string;
  showEvidenceField?: boolean;
  evidenceLabel?: string;
  evidenceRequired?: boolean;
  evidencePlaceholder?: string;
  evidenceHelpText?: string;
}

const DirectHandlingFields: React.FC<Props> = ({
  noteHtml,
  evidenceUrl,
  onNoteChange,
  onEvidenceUrlChange,
  noteError,
  evidenceError,
  visibilityHint,
  disabled = false,
  noteLabel = 'Catatan Penanganan Langsung',
  noteRequired = true,
  notePlaceholder = 'Tuliskan notulensi singkat, keputusan, tindak lanjut, PIC, dan target penyelesaian...',
  noteHelperText = 'Gunakan editor ini agar keputusan penanganan rapi saat dibaca pimpinan pada detail laporan.',
  guidanceTitle = 'Yang Perlu Ditulis',
  guidanceItems = [
    'Ringkasan masalah dan keputusan yang diambil.',
    'Tindakan yang sudah atau akan dijalankan.',
    'PIC internal dan target penyelesaiannya.',
  ],
  accessTitle = 'Akses Pimpinan',
  accessHint,
  showEvidenceField = true,
  evidenceLabel = 'Tautan Bukti Dokumen',
  evidenceRequired = true,
  evidencePlaceholder = 'https://drive.google.com/... atau tautan dokumen lain yang bisa diakses pimpinan',
  evidenceHelpText = 'Gunakan tautan Google Drive, OneDrive, Dropbox, atau repositori dokumen lain yang bisa dibuka pimpinan terkait.',
}) => {
  const effectiveAccessHint = accessHint || visibilityHint;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
            {noteLabel}
            {noteRequired && <span className="text-danger ml-1">*</span>}
          </label>
          <TipTapEditor
            content={noteHtml}
            onChange={onNoteChange}
            placeholder={notePlaceholder}
            editable={!disabled}
            minHeight="240px"
            className={noteError ? 'border-danger' : ''}
          />
          {noteError ? (
            <p className="text-xs text-danger">{noteError}</p>
          ) : noteHelperText ? (
            <p className="text-xs text-neutral-text-secondary">
              {noteHelperText}
            </p>
          ) : null}
        </div>

        <div className="space-y-3">
          <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-4">
            <div className="flex items-start gap-2">
              <div className="mt-0.5 text-blue-700">
                <FileText className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-blue-800">
                  {guidanceTitle}
                </p>
                <ul className="mt-2 list-disc space-y-1 pl-4 text-xs leading-relaxed text-blue-800">
                  {guidanceItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-4">
            <div className="flex items-start gap-2">
              <div className="mt-0.5 text-emerald-700">
                <Eye className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-800">
                  {accessTitle}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-emerald-800">
                  {effectiveAccessHint}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showEvidenceField && (
        <Input
          label={evidenceLabel}
          required={evidenceRequired}
          disabled={disabled}
          value={evidenceUrl}
          onChange={(event) => onEvidenceUrlChange?.(event.target.value)}
          placeholder={evidencePlaceholder}
          helpText={evidenceHelpText}
          error={evidenceError}
          icon={<Link2 className="w-4 h-4" />}
        />
      )}
    </div>
  );
};

export default DirectHandlingFields;
